import {
  APPLICATION_STATUS_TO_SHOW_CONTACT_DETAILS,
  ASTERIKS_FOR_EMAIL,
  ASTERIKS_FOR_PHONE_NUMBER,
} from '@application/constants';
import { useRouter } from '@application/utils';
import type { MtApplicationBreederGetApplicationQuery } from '@gql/graphql';
import { CountryCode } from '@gql/graphql';
import { PLACEHOLDER_IMAGE_DOG, PLACEHOLDER_IMAGE_USER } from '@presentation/constants';
import type { MtApplicationPageApplication } from '@presentation/pages';
import dayjs from 'dayjs';
import { useCallback } from 'react';

export const useFormatApplication = () => {
  const { translate } = useRouter();

  const formatEnum = useCallback(
    (translationKey: string, values: any[]) => {
      return values
        .map((x) => translate(`${translationKey}.${x}`))
        .map((x) => `- ${x}`)
        .join('<br/>');
    },
    [translate],
  );

  const formatGenderPreference = useCallback(
    (translationKey: string, values: any[]) => {
      if (values.length > 1) {
        return `- ${translate('enum.gender.no_preference')}`;
      }
      return values
        .map((x) => translate(`${translationKey}.${x}`))
        .map((x) => `- ${x}`)
        .join('<br/>');
    },
    [translate],
  );

  const formatApplication = useCallback(
    (
      data?: MtApplicationBreederGetApplicationQuery | null,
    ): MtApplicationPageApplication | null => {
      const application = data?.accountMe?.dogBreeder?.applications?.[0];
      if (!application) return null;

      const { account: person, animal, breedPreference: breed } = application;

      const differentCountryCode =
        data?.accountMe?.address?.country &&
        data?.accountMe?.address?.country !== person.address?.country
          ? person.address?.country
          : undefined;

      const getPerson = () => {
        const image = PLACEHOLDER_IMAGE_USER;
        const title = `${person.firstName} ${person.lastName}`;
        const hideDetails = !APPLICATION_STATUS_TO_SHOW_CONTACT_DETAILS.includes(
          application.status,
        );

        if (hideDetails) {
          return {
            image,
            title,
            phoneNumber: ASTERIKS_FOR_PHONE_NUMBER,
            link: undefined,
            email: ASTERIKS_FOR_EMAIL,
          };
        }

        return {
          image,
          title: `${person.firstName} ${person.lastName}`,
          phoneNumber: person.phone ?? undefined,
          link: undefined,
          email: person.email ?? undefined,
        };
      };

      return {
        animal: animal
          ? {
              dateOfBirth: animal.birthday ? dayjs(animal.birthday) : null,
              name: animal.name ?? null,
            }
          : undefined,
        details: [
          {
            label: translate('glossary.home'),
            value: [
              {
                label: translate('common.inputs.accomodation.label'),
                value: translate(
                  `enum.dogBreederApplicationAccommodation.${application.accommodation}`,
                ),
              },
              {
                label: translate('common.inputs.otherAnimals.label'),
                value: formatEnum(
                  'enum.dogBreederApplicationOtherAnimal',
                  application.otherAnimals,
                ),
              },
            ],
          },
          {
            label: translate('glossary.puppyCare'),
            value: [
              {
                label: translate('common.inputs.trainers.label'),
                value: formatEnum('enum.dogBreederApplicationTrainer', application.trainers),
              },
              {
                label: translate('common.inputs.timeAlone.label'),
                value: translate(`enum.dogBreederApplicationTimeAlone.${application.timeAlone}`),
              },
              {
                label: translate('common.inputs.experiences.label'),
                value: formatEnum('enum.dogBreederApplicationExperience', application.experiences),
              },
            ],
          },
          {
            label: translate('glossary.preferences'),
            value: [
              {
                label: translate('common.inputs.motive.label'),
                value: application.motive ?? '-',
              },
              {
                label: translate('common.inputs.genderPreferences.label'),
                value: formatGenderPreference('enum.gender', application.genderPreferences),
              },
            ],
          },
        ],
        status: application.status,
        createdAt: application.createdAt,
        breed: {
          title: breed.translations?.[0]?.name ?? '-',
          image: breed.imgs?.[0]?.small ?? PLACEHOLDER_IMAGE_DOG,
        },
        person: getPerson(),
        differentCountryWarning: differentCountryCode
          ? translate('mt.breeder.applications.differentCountryWarning').replace(
              '$country',
              translate(`enum.country.${differentCountryCode ?? CountryCode.BE}`),
            )
          : undefined,
      };
    },
    [formatEnum, formatGenderPreference, translate],
  );

  return {
    formatApplication,
  };
};
